import React from 'react';
import { MovieScene } from '../../services/api/movieService';
import { ImageIcon, RefreshCw, Loader2, AlertCircle, Sparkles } from 'lucide-react';

/**
 * ステップ②: 絵コンテ。
 *
 * シーンごとに開始フレーム（静止画）を生成する。
 * 生成中のシーンがあってもページ全体はブロックせず、
 * 各カードが自分の状態（none/pending/processing/completed/failed）を表示する。
 */

interface StoryboardStepProps {
  scenes: MovieScene[];
  aspectRatio: string;
  generatingSceneIds: Set<string>;
  onGenerate: (sceneId: string) => void;
  onGenerateAll: () => void;
}

const aspectClass = (ratio: string): string => {
  switch (ratio) {
    case '9:16':
      return 'aspect-[9/16]';
    case '1:1':
      return 'aspect-square';
    default:
      return 'aspect-video';
  }
};

const StoryboardStep: React.FC<StoryboardStepProps> = ({
  scenes,
  aspectRatio,
  generatingSceneIds,
  onGenerate,
  onGenerateAll,
}) => {
  if (scenes.length === 0) {
    return (
      <div className="text-center py-12 text-gray-400 text-sm border border-dashed border-gray-300 rounded-xl">
        先に「① 台本・構成」でシーンを作成してください
      </div>
    );
  }

  const pendingScenes = scenes.filter(
    (s) => s.storyboard.status === 'none' || s.storyboard.status === 'failed'
  );
  const activeCount = scenes.filter(
    (s) => s.storyboard.status === 'pending' || s.storyboard.status === 'processing'
  ).length;

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <p className="text-sm text-gray-600">
          各シーンの開始フレームを生成します。
        </p>
        <button
          onClick={onGenerateAll}
          disabled={pendingScenes.length === 0}
          className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-primary text-white rounded-lg hover:opacity-90 disabled:opacity-50 shrink-0 ml-4"
        >
          <Sparkles className="w-4 h-4" />
          未生成を一括生成（{pendingScenes.length}件）
        </button>
      </div>

      {activeCount > 0 && (
        <div className="mb-4 p-2.5 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-700 flex items-center gap-2">
          <Loader2 className="w-4 h-4 animate-spin" />
          {activeCount}件の絵コンテを生成中です。完了すると自動で表示されます。
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {scenes.map((scene, index) => {
          const status = scene.storyboard.status;
          const isRequesting = generatingSceneIds.has(scene.id);
          const isActive = status === 'pending' || status === 'processing' || isRequesting;

          return (
            <div key={scene.id} className="border border-warm-border rounded-xl overflow-hidden bg-white">
              <div className={`${aspectClass(aspectRatio)} bg-gray-100 relative flex items-center justify-center`}>
                {scene.storyboard.image ? (
                  <img
                    src={scene.storyboard.image.url}
                    alt={scene.summary ?? `シーン${index + 1}`}
                    className="w-full h-full object-cover"
                  />
                ) : isActive ? (
                  <div className="flex flex-col items-center text-gray-400">
                    <Loader2 className="w-7 h-7 animate-spin mb-2" />
                    <span className="text-xs">生成中...</span>
                  </div>
                ) : status === 'failed' ? (
                  <div className="flex flex-col items-center text-red-400 px-4 text-center">
                    <AlertCircle className="w-7 h-7 mb-2" />
                    <span className="text-xs">{scene.storyboard.error_message ?? '生成に失敗しました'}</span>
                  </div>
                ) : (
                  <ImageIcon className="w-9 h-9 text-gray-300" />
                )}
              </div>

              <div className="p-3">
                <div className="flex items-center justify-between mb-1.5">
                  <span className="text-xs font-semibold text-gray-700">
                    シーン {index + 1}
                    <span className="ml-1.5 font-normal text-gray-400">{scene.duration_sec}秒</span>
                  </span>
                  <button
                    onClick={() => onGenerate(scene.id)}
                    disabled={isActive || !scene.storyboard_prompt}
                    className="flex items-center gap-1 px-2 py-1 text-xs border border-gray-300 rounded-lg hover:bg-gray-100 disabled:opacity-50"
                    title={!scene.storyboard_prompt ? '①でプロンプトを入力してください' : undefined}
                  >
                    <RefreshCw className={`w-3 h-3 ${isRequesting ? 'animate-spin' : ''}`} />
                    {scene.storyboard.image || status === 'failed' ? '再生成' : '生成'}
                  </button>
                </div>
                {scene.summary && (
                  <p className="text-xs text-gray-600 line-clamp-2">{scene.summary}</p>
                )}
                {scene.storyboard_prompt && (
                  <details className="mt-1.5">
                    <summary className="text-[11px] text-gray-400 cursor-pointer select-none">プロンプト</summary>
                    <p className="text-[11px] text-gray-500 mt-1 whitespace-pre-wrap">{scene.storyboard_prompt}</p>
                  </details>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default StoryboardStep;
